import { useEffect, useRef } from 'react'
import { io, type Socket } from 'socket.io-client'
import { useQueryClient } from '@tanstack/react-query'
import { env } from '@/env'
import { useAuthStore } from '@/stores/auth'

export type RealtimeEvent =
  | { type: 'case:created'; tenantId: string; case: { id: string; number: number; subject: string } }
  | { type: 'case:updated'; tenantId: string; caseId: string }
  | { type: 'case:sla_breach'; tenantId: string; caseId: string }
  | { type: 'omni:call_start'; tenantId: string; sessionId: string; contactId: string | null }
  | { type: 'omni:call_end'; tenantId: string; sessionId: string; caseId: string | null }
  | { type: 'agent:state_change'; tenantId: string; userId: string; state: string }
  | { type: 'sync:health_change'; tenantId: string; status: 'healthy' | 'degraded' | 'down' }
  // Live webchat
  | { type: 'livechat:new';          tenantId: string; chatId: string; sessionId: string; visitorId: string; visitorName: string | null; queuePosition: number; botTranscript: Array<{ role: string; content: string }> }
  | { type: 'livechat:incoming';     tenantId: string; agentId: string; chatId: string; sessionId: string; visitorName: string | null; visitorCountry: string | null; lastMessage: string | null; priority: string; waitTime: number }
  | { type: 'livechat:accepted';     tenantId: string; chatId: string; agentId: string; agentName: string }
  | { type: 'livechat:message';      tenantId: string; chatId: string; sessionId: string; role: 'agent' | 'visitor' | 'system'; content: string; agentId: string | null; agentName: string | null; timestamp: string }
  | { type: 'livechat:visitor_message'; tenantId: string; chatId: string; content: string; visitorName: string | null; timestamp: string }
  | { type: 'livechat:typing';       tenantId: string; chatId: string; sessionId: string; from: 'agent' | 'visitor'; name: string | null; isTyping: boolean }
  | { type: 'livechat:resolved';     tenantId: string; chatId: string; sessionId: string; message: string }
  | { type: 'livechat:closed';       tenantId: string; chatId: string }
  | { type: 'livechat:transferred';  tenantId: string; chatId: string; fromAgentId: string; toAgentId: string; note: string | null }
  | { type: 'livechat:queue_update'; tenantId: string; chatId: string; queuePosition: number }
  | { type: 'presence:online';       tenantId: string; agentId: string }
  | { type: 'presence:offline';      tenantId: string; agentId: string }
  // Unified Inbox (3A)
  | { type: 'inbox:updated';         tenantId: string; conversationId: string; channel: string; preview: string; direction: 'inbound' | 'outbound' }
  // AI prerequisites (Phase 4) — alert pushed to the recipient's personal room
  | { type: 'ai:alert'; id: string; tenantId: string; recipientId: string; alertType: string; severity: string; title: string; message: string; suggestedAction: string | null; actionUrl: string | null; entityType: string; entityId: string; entityName: string; createdAt: string }
  // Marketing 1F — MQL→SQL handoff pushed to the owning sales rep
  | { type: 'lead:handoff'; tenantId: string; leadId: string; leadName: string; assigneeId: string; at: string }

let socket: Socket | null = null

function getSocket(token: string): Socket {
  if (socket && socket.connected) return socket
  if (socket) socket.disconnect()
socket = io(env.VITE_WS_URL, {
    path: '/socket.io',
    auth: { token },
    transports: ['polling', 'websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 10_000,
  })
  return socket
}

/**
 * Connect the shared socket once the user is authenticated. On every event
 * we invalidate the matching React Query so the UI converges without custom
 * per-page wiring. Specific pages (dashboard live feed, case detail) can
 * subscribe to additional events via `useSocketEvent`.
 */
export function useSocket() {
  const token = useAuthStore((s) => s.accessToken)
  const qc = useQueryClient()

  useEffect(() => {
    if (!token) return
    const s = getSocket(token)

    const onCreated = () => {
      qc.invalidateQueries({ queryKey: ['cases'] })
      qc.invalidateQueries({ queryKey: ['dash-recent-cases'] })
      qc.invalidateQueries({ queryKey: ['tenant', 'usage'] })
    }
    const onUpdated = ({ caseId }: { caseId: string }) => {
      qc.invalidateQueries({ queryKey: ['case', caseId] })
      qc.invalidateQueries({ queryKey: ['cases'] })
    }
    const onSlaBreach = () => {
      qc.invalidateQueries({ queryKey: ['cases'] })
      qc.invalidateQueries({ queryKey: ['dash-recent-cases'] })
    }
    const onHealth = () => {
      qc.invalidateQueries({ queryKey: ['telephony', 'health'] })
      qc.invalidateQueries({ queryKey: ['dash-omni-health'] })
    }
    const onOmni = () => {
      qc.invalidateQueries({ queryKey: ['cases'] })
    }

    const onNotificationNew = (payload: unknown) => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      // Custom DOM event → lets NotificationBell and any toast host react
      // without coupling this hook to those components directly.
      window.dispatchEvent(new CustomEvent('vela:notification', { detail: payload }))
      // Sprint 2 — optional desktop + sound notification (email events et al).
      maybeDesktopNotify(payload)
    }

    // Live-chat events — invalidate the queue + usage (the sidebar badge)
    // whenever anything changes so the inbox reflects the new state instantly.
    const onLiveChat = () => {
      qc.invalidateQueries({ queryKey: ['live-chat', 'queue'] })
      qc.invalidateQueries({ queryKey: ['live-chat', 'stats'] })
      qc.invalidateQueries({ queryKey: ['tenant', 'usage'] })
    }
    const onLiveChatMessage = (e: { chatId: string }) => {
      qc.invalidateQueries({ queryKey: ['live-chat', 'messages', e.chatId] })
    }

    // Presence heartbeat: the server stamps `agent:{tenant}:{id}` in Redis with
    // a 60s TTL on connect, but a long-lived socket never reconnects, so the
    // key would lapse and the agent would wrongly look offline (breaking chat
    // assignment + the incoming-chat alert). Refresh it every 30s. `connect`
    // also re-pings after any reconnect.
    s.emit('presence:ping')
    const presenceTimer = window.setInterval(() => s.emit('presence:ping'), 30_000)
    const onConnect = () => s.emit('presence:ping')
    s.on('connect', onConnect)

    s.on('case:created', onCreated)
    s.on('case:updated', onUpdated)
    s.on('case:sla_breach', onSlaBreach)
    s.on('sync:health_change', onHealth)
    s.on('omni:call_start', onOmni)
    s.on('omni:call_end', onOmni)
    s.on('notification:new', onNotificationNew)
    s.on('livechat:new',        onLiveChat)
    s.on('livechat:incoming',   onLiveChat)
    s.on('livechat:accepted',   onLiveChat)
    s.on('livechat:resolved',   onLiveChat)
    s.on('livechat:closed',     onLiveChat)
    s.on('livechat:queue_update', onLiveChat)
    s.on('livechat:message',    onLiveChatMessage)
    s.on('livechat:visitor_message', onLiveChatMessage)

    return () => {
      window.clearInterval(presenceTimer)
      s.off('connect', onConnect)
      s.off('case:created', onCreated)
      s.off('case:updated', onUpdated)
      s.off('case:sla_breach', onSlaBreach)
      s.off('sync:health_change', onHealth)
      s.off('omni:call_start', onOmni)
      s.off('omni:call_end', onOmni)
      s.off('notification:new', onNotificationNew)
      s.off('livechat:new', onLiveChat)
      s.off('livechat:incoming', onLiveChat)
      s.off('livechat:accepted', onLiveChat)
      s.off('livechat:resolved', onLiveChat)
      s.off('livechat:closed', onLiveChat)
      s.off('livechat:queue_update', onLiveChat)
      s.off('livechat:message', onLiveChatMessage)
      s.off('livechat:visitor_message', onLiveChatMessage)
    }
  }, [token, qc])

  // Disconnect on logout.
  useEffect(() => {
    if (!token && socket) {
      socket.disconnect()
      socket = null
    }
  }, [token])
}

/**
 * Subscribe to a single event type for the life of the component. Useful for
 * triggering toasts or ephemeral UI flashes (e.g. the dashboard live feed
 * row-flash animation) that shouldn't go through React Query invalidation.
 */
export function useSocketEvent<T extends RealtimeEvent['type']>(
  type: T,
  handler: (event: Extract<RealtimeEvent, { type: T }>) => void,
) {
  const token = useAuthStore((s) => s.accessToken)
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    if (!token) return
    const s = getSocket(token) as Socket & {
      on: (ev: string, fn: (...a: unknown[]) => void) => void
      off: (ev: string, fn: (...a: unknown[]) => void) => void
    }
    const fn = (...args: unknown[]) =>
      handlerRef.current(args[0] as Extract<RealtimeEvent, { type: T }>)
    s.on(type, fn)
    return () => { s.off(type, fn) }
  }, [type, token])
}

/**
 * Tenant Email System (Sprint 2) — optional desktop + sound notification.
 *
 * Gated by localStorage toggles ('vela.notify.desktop' / 'vela.notify.sound',
 * both default-on unless explicitly set to 'off') and the browser Notification
 * permission. Fully feature-detected and wrapped in try/catch so it can never
 * break the realtime pipeline.
 */
function maybeDesktopNotify(payload: unknown): void {
  try {
    const p = (payload ?? {}) as { title?: string; body?: string }
    const title = p.title || 'New notification'
    if (localStorage.getItem('vela.notify.sound') !== 'off') playBeep()
    if (localStorage.getItem('vela.notify.desktop') === 'off') return
    if (typeof Notification === 'undefined') return
    if (Notification.permission === 'granted') {
      new Notification(title, { body: p.body ?? '', icon: '/favicon.ico', tag: 'vela-notification' })
    } else if (Notification.permission === 'default') {
      // Ask once; the next notification will surface if the user grants it.
      void Notification.requestPermission().catch(() => undefined)
    }
  } catch {
    /* never throw out of the socket handler */
  }
}

function playBeep(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = 880
    gain.gain.value = 0.04
    osc.start()
    osc.stop(ctx.currentTime + 0.12)
    osc.onended = () => ctx.close().catch(() => undefined)
  } catch {
    /* audio not available */
  }
}
